import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

const wrap = (path: React.ReactNode, fill = false) =>
  function Icon({ size = 18, ...rest }: IconProps) {
    return (
      <svg
        viewBox="0 0 24 24"
        width={size}
        height={size}
        fill={fill ? 'currentColor' : 'none'}
        stroke={fill ? 'none' : 'currentColor'}
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
        {...rest}
      >
        {path}
      </svg>
    );
  };

export const Ic = {
  plus: wrap(<path d="M12 5v14M5 12h14" />),
  search: wrap(<><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>),
  bell: wrap(<><path d="M18 16v-5a6 6 0 1 0-12 0v5l-1.5 2h15z" /><path d="M10 20a2 2 0 0 0 4 0" /></>),
  calendar: wrap(<><rect x="3.5" y="5" width="17" height="15" rx="2" /><path d="M3.5 10h17M8 3v4M16 3v4" /></>),
  trophy: wrap(<><path d="M7 4h10v5a5 5 0 0 1-10 0z" /><path d="M5 6H3a3 3 0 0 0 3 3M19 6h2a3 3 0 0 1-3 3M9 21h6M12 15v6" /></>),
  users: wrap(<><circle cx="9" cy="9" r="3.5" /><path d="M3 20a6 6 0 0 1 12 0" /><circle cx="17" cy="8" r="3" /><path d="M15.5 20h5.5a5 5 0 0 0-5-5" /></>),
  user: wrap(<><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></>),
  lock: wrap(<><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></>),
  unlock: wrap(<><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0" /></>),
  globe: wrap(<><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18" /></>),
  chevR: wrap(<path d="m9 6 6 6-6 6" />),
  chevL: wrap(<path d="m15 6-6 6 6 6" />),
  chevD: wrap(<path d="m6 9 6 6 6-6" />),
  check: wrap(<path d="m5 13 4 4 10-10" />),
  x: wrap(<path d="m6 6 12 12M18 6 6 18" />),
  copy: wrap(<><rect x="8" y="8" width="12" height="12" rx="2" /><path d="M4 16V6a2 2 0 0 1 2-2h10" /></>),
  share: wrap(<><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><path d="m8.6 13.5 6.8 4M15.4 6.5l-6.8 4" /></>),
  mail: wrap(<><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>),
  link: wrap(<><path d="M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 1 0-5.66-5.66l-1 1" /><path d="M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 1 0 5.66 5.66l1-1" /></>),
  home: wrap(<path d="M3 11 12 4l9 7v9a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z" />),
  bolt: wrap(<path d="M13 2 3 14h7l-1 8 10-12h-7z" />, true),
  edit: wrap(<><path d="M4 20h4l10-10-4-4L4 16z" /><path d="m14 6 4 4" /></>),
  trash: wrap(<><path d="M4 7h16M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2M6 7l1 13a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-13" /></>),
  filter: wrap(<path d="M4 5h16l-6 8v6l-4-2v-4z" />),
  clock: wrap(<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>),
  grid: wrap(<><rect x="4" y="4" width="7" height="7" rx="1.5" /><rect x="13" y="4" width="7" height="7" rx="1.5" /><rect x="4" y="13" width="7" height="7" rx="1.5" /><rect x="13" y="13" width="7" height="7" rx="1.5" /></>),
  rows: wrap(<><rect x="4" y="5" width="16" height="4" rx="1.5" /><rect x="4" y="11" width="16" height="4" rx="1.5" /><rect x="4" y="17" width="16" height="2.5" rx="1" /></>),
  timeline: wrap(<><path d="M4 6h16M4 12h16M4 18h16" /><circle cx="8" cy="6" r="2" fill="currentColor" /><circle cx="15" cy="12" r="2" fill="currentColor" /><circle cx="10" cy="18" r="2" fill="currentColor" /></>),
  menu: wrap(<path d="M4 7h16M4 12h16M4 17h16" />),
  ball: wrap(<><circle cx="12" cy="12" r="9" /><path d="m12 3 3 4-1 5-4 1-3-4 2-5zM3.5 11l4 2M20.5 11l-4 2M12 21l1-4" /></>),
  logout: wrap(<><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="m16 17 5-5-5-5M21 12H9" /></>),
};
